import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Globe } from 'lucide-react';
import { scanApi } from '@/services/api';
import { ScanStatistics } from '@/types';

interface AnalyticsWithTransitionProps {
  className?: string;
  variant?: 'header' | 'hero' | 'results';
  show?: boolean;
}

const fmt = (n: number) => n > 999 ? `${(n / 1000).toFixed(1)}k` : String(n);

const AnalyticsWithTransition: React.FC<AnalyticsWithTransitionProps> = ({
  className = '',
  variant = 'header',
  show = true
}) => {
  const [stats, setStats] = useState<ScanStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await scanApi.getStatistics();
        setStats(data);
      } catch {
        // stats are decorative — silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!show) return null;

  // Hero variant: large stat blocks for the dark stats bar
  if (variant === 'hero') {
    if (loading || !stats) {
      return (
        <div className={`flex gap-4 ${className}`}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="w-20 h-20 bg-gray-800 rounded-md animate-pulse" />
          ))}
        </div>
      );
    }

    const items = [
      { label: 'Total Scanned', value: stats.total,      color: 'text-blue-400',    bg: 'bg-blue-500/20',    icon: <Globe       className="h-5 w-5" /> },
      { label: 'Available',     value: stats.available,  color: 'text-emerald-400', bg: 'bg-emerald-500/20', icon: <CheckCircle className="h-5 w-5" /> },
      { label: 'Registered',    value: stats.registered, color: 'text-amber-400',   bg: 'bg-amber-500/20',   icon: <XCircle     className="h-5 w-5" /> },
      { label: 'Errors',        value: stats.errors,     color: 'text-red-400',     bg: 'bg-red-500/20',     icon: <AlertCircle className="h-5 w-5" /> },
    ];

    return (
      <div className={`flex gap-4 ${className}`}>
        {items.map(item => (
          <div
            key={item.label}
            className={`w-20 h-20 ${item.bg} rounded-md flex flex-col items-center justify-center gap-1 transition-all duration-200 hover:scale-105 cursor-default`}
          >
            <span className={item.color}>{item.icon}</span>
            <span className={`text-sm font-bold ${item.color}`}>{fmt(item.value)}</span>
          </div>
        ))}
      </div>
    );
  }

  // Header / results variant: compact inline row
  if (loading || !stats) return null;

  const items = [
    { label: 'Total',      value: stats.total,      color: 'text-blue-500'    },
    { label: 'Available',  value: stats.available,  color: 'text-emerald-500' },
    { label: 'Registered', value: stats.registered, color: 'text-amber-500'   },
  ];

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {items.map(item => (
        <div key={item.label} className="flex flex-col items-center">
          <span className={`text-sm font-bold ${item.color}`}>{fmt(item.value)}</span>
          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

export default AnalyticsWithTransition;
